import { Kbd } from '@mantine/core';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useKeyboardShortcut } from 'ts/base/hooks/UseKeyboardShortcut';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { KeyboardShortcutRegistry } from 'ts/base/scaffolding/KeyboardShortcutRegistry';
import { HelpIcon } from 'ts/commons/components/HelpIcon';
import { StringUtils } from 'ts/commons/StringUtils';
import { UIUtils } from 'ts/commons/UIUtils';
import { Button } from 'ts/components/Button';
import { Form, FormCheckbox, FormField } from 'ts/components/Form';
import { Message } from 'ts/components/Message';
import { Modal, ModalActionButtons } from 'ts/components/Modal';
import { TextArea } from 'ts/components/TextArea';
import { useBlacklistPermissions } from 'ts/perspectives/findings/details/tabs/commons/UseBlacklistPermissions';
import { useBlacklistMutation } from 'ts/perspectives/findings/details/tabs/FindingDetailsSiblingsTabBlacklistButtons';
import { EFindingBlacklistOperation } from 'typedefs/EFindingBlacklistOperation';
import { EFindingBlacklistType, type EFindingBlacklistTypeEntry } from 'typedefs/EFindingBlacklistType';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import type { TrackedFindingWithDiffInfo } from 'typedefs/TrackedFindingWithDiffInfo';

const HELPER_TEXT_BY_FLAGGING_TYPE: Record<EFindingBlacklistTypeEntry, string> = {
	FALSE_POSITIVE:
		'You can mark a finding as a false positive if you think the finding is incorrect, that is, the static analysis produced a finding even though there is no violation present.',
	TOLERATION:
		'You can tolerate a finding if the finding is technically correct, but it cannot or should not be resolved, e.g., due to a disproportionate required effort. This will exclude it from all finding lists.'
};

type ExclusionRationaleModalProps = {
	findingsToExclude: TrackedFindingWithDiffInfo[] | TrackedFinding[];
	exclusionType: EFindingBlacklistType;
	/** The commit that is used to flag findings. */
	commit: UnresolvedCommitDescriptor | null;
	onClose: () => void;
	onSuccess?: () => void;
	updateQueriesOptimistically: boolean;
};

/** Renders modal for entering a blacklisting rationale. */
export function ExclusionRationaleModal({
	exclusionType,
	findingsToExclude,
	commit,
	onClose,
	onSuccess,
	updateQueriesOptimistically = true
}: ExclusionRationaleModalProps): JSX.Element {
	const [rationale, setRationale] = useState<string>('');
	const [autoApprove, setAutoApprove] = useState<boolean>(false);
	const project = useProjectId();

	const mutation = useBlacklistMutation(
		findingsToExclude.map(v => v.id),
		commit,
		onSuccess,
		updateQueriesOptimistically
	);
	const { detailedPermissions, disableEmptyRationale } = useBlacklistPermissions(findingsToExclude);
	const permission = detailedPermissions[exclusionType.name];

	const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

	const onSubmit = () => {
		if (disableEmptyRationale && StringUtils.isEmptyOrWhitespace(rationale)) {
			setShowErrorMessage(true);
			return;
		}
		setShowErrorMessage(false);

		mutation.mutate(
			{
				pathParams: { project },
				queryParams: {
					t: commit ?? undefined,
					operation: EFindingBlacklistOperation.ADD.name,
					type: exclusionType.name
				},
				body: {
					findingIds: findingsToExclude.map(finding => finding.id),
					blacklistInfo: { rationale },
					autoApprove: permission.includes('Suggest') ? autoApprove : true
				}
			},
			{
				onSettled: () => onClose()
			}
		);
	};

	useKeyboardShortcut(`${KeyboardShortcutRegistry.CMD_CTRL} ENTER`, 'Saving blacklist rationale', onSubmit);

	return (
		<Modal opened onClose={onClose} size="xl" title="Enter rationale" data-testid="flagging-rationale-modal">
			<Form error id="flagging-rationale-form" onSubmit={onSubmit}>
				{showErrorMessage ? <Message error content="Rationale must not be empty." /> : null}
				<FormField>
					{permission.includes('Suggest') ? (
						<Message info>Finding exclusion is subject to review in order to come effective</Message>
					) : null}
					<p>
						<label>
							Rationale for{' '}
							{exclusionType === EFindingBlacklistType.TOLERATION ? 'tolerating' : 'marking'}{' '}
							{findingsToExclude.length > 1
								? `these ${findingsToExclude.length} findings`
								: 'this finding'}
							{exclusionType === EFindingBlacklistType.FALSE_POSITIVE
								? ` as ${StringUtils.pluralize(findingsToExclude, 'false positive')}`
								: ''}
							{disableEmptyRationale ? '' : ' (optional)'}{' '}
							<HelpIcon
								textOrMarkdown={`${HELPER_TEXT_BY_FLAGGING_TYPE[exclusionType.name]} This operation will be recorded with user, date and rationale for later reference.`}
							/>
						</label>
					</p>
					<TextArea
						data-autofocus
						placeholder={
							exclusionType === EFindingBlacklistType.FALSE_POSITIVE
								? 'Why is this a false positive?'
								: 'Why should this finding be tolerated?'
						}
						data-testid="rationale-text"
						rows={3}
						onChange={e => setRationale(e.target.value)}
						onKeyDown={(event: KeyboardEvent) => {
							if (UIUtils.isCtrlKey(event) && event.code === 'Enter') {
								onSubmit();
							}
						}}
					/>
				</FormField>
				{permission.includes('Mark') && permission.includes('Suggest') ? (
					<FormCheckbox
						label="Skip finding exclusion approval workflow"
						data-testid="skip-approval-workflow-checkbox"
						checked={autoApprove}
						onChange={(_, data) => setAutoApprove(data.checked ?? false)}
					/>
				) : null}
			</Form>
			<p className="italic py-1 text-[#666]">
				Hint: Marking multiple findings may take a while. Press
				<Kbd className="font-normal">{KeyboardShortcutRegistry.CMD_CTRL}</Kbd>+<Kbd>Enter</Kbd> to save.
			</p>
			<ModalActionButtons>
				<Button
					primary
					type="submit"
					content="OK"
					className={'!w-40 ' + (mutation.isPending ? 'loading' : '')}
					data-testid="flagging-rationale-modal-ok"
					form="flagging-rationale-form"
					disabled={mutation.isPending}
				/>
				<Button content="Cancel" className="!w-40" disabled={mutation.isPending} onClick={onClose} />
			</ModalActionButtons>
		</Modal>
	);
}
